import * as React from "react";
import * as TreeRepository from "../../Repository/TreeRepository";
import ConfigurationService from "../../Service/ConfigurationService";
import { collapseModalAtPoint } from "../../Helper/PopupCollapse";
import { Carousel, Modal, Pagination } from "antd";
import { Photo } from "tree-wallet-client";
import { useEffect, useRef, useState } from "react";
import { Spin } from "antd/es";
import PhotoViewer from "../../Component/PhotoViewer";
import CarouselItem from "../MapPage/Popup/CarouselItem";

interface MapPopupProps {
  language: string;
  treeId: number;
  user?: any;
  contract?: any;
  onClose?: () => void;
  // Where this tree's icon sits on screen: the closing animation is directed at it.
  getMarkerScreenPoint?: () => { x: number; y: number } | null;
}

function getImageUrl(photos?: Photo[]): string | void {
  if (photos && photos.length > 0) {
    return photos[0].imageUrl;
  }
}

export default function PublicPageMapPopup(props: MapPopupProps) {
  const { treeId } = props;
  const [open, setOpen] = useState<boolean>(true);
  const [photos, setPhotos] = useState<any>(undefined);
  const [tree, setTree] = useState<any>(undefined);
  const [treeType, setTreeType] = useState<any>(undefined);

  // Any node inside the window is enough for the collapse: the panel it is nested in is looked up
  // from it. Hidden, so it takes no room in the modal body.
  const collapseAnchorRef = useRef<HTMLSpanElement>(null);

  const close = () => {
    collapseModalAtPoint(collapseAnchorRef.current, props.getMarkerScreenPoint?.() ?? null);
    setOpen(false);
  };

  const imageUrl = getImageUrl(photos);
  const fullImageUrl = `${ConfigurationService.urls().baseApiUrl}${imageUrl}`;

  // Same order as the private map popup: oldest first, so the carousel opens on the newest photo.
  const sortedPhotos: any[] = Array.isArray(photos)
    ? [...photos].sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    : [];

  // Photo shown by the full screen viewer; empty means the viewer is closed.
  const [viewerUrl, setViewerUrl] = useState<string | undefined>(undefined);
  // Photo shown by the carousel, newest last (see `sortedPhotos`) - like the private map popup.
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0);
  const carouselRef = useRef<any>(null);

  const onPageChange = (page: number) => {
    setCurrentPhotoIndex(page - 1);
    carouselRef.current?.goTo(page - 1);
  };

  useEffect(() => {
    (async () => {
      try {
        const tree = await TreeRepository.getPublicTree({
          params: { treeId }
        });
        setTree(tree);
        setTreeType(tree.treeType);
        const treePhotos = tree.photos;
        setPhotos(treePhotos);
        // Same default as the private map popup: the newest photo, i.e. the last of the sorted list.
        if (treePhotos && treePhotos.length > 0) {
          setCurrentPhotoIndex(treePhotos.length - 1);
        }
      } catch (err) {
        console.error(err);
      }
    })()
  }, [])

  return (
    <Modal
      open={open}
      maskClosable={false}
      onCancel={close}
      onOk={close}
      afterClose={props.onClose}
      cancelButtonProps={{ style: { display: 'none' } }}
      // Same as the private map popup: the map around the modal claims touch gestures, the carousel
      // needs the browser's own swipe back.
      bodyStyle={{ touchAction: 'auto' }}
    >
      <span ref={collapseAnchorRef} style={{ display: 'none' }} />
      {!tree
        ? <div className="map-tree description" style={{ textAlign: 'center' }}>
          <h3>Loading data... <Spin /></h3>
        </div>
        : <div className="map-tree description" style={{ textAlign: 'center' }}>
          {sortedPhotos.length > 1
            ? (
              <>
                {/* The carousel's own arrows draw no arrow glyph (antd 4 ships the carousel arrow
                    rules without the anticon font), so this counter - the same control as the
                    private map popup - is what shows the photo can be changed. */}
                <Pagination
                  onChange={onPageChange}
                  current={currentPhotoIndex + 1}
                  total={sortedPhotos.length}
                  pageSize={1}
                  size="small"
                />
                <Carousel
                  ref={carouselRef}
                  // Same carousel as the private map popup: it opens on the newest photo, the counter
                  // above and the swipe move through the others. `touchAction: auto` keeps the swipe
                  // gesture in the browser instead of letting the map underneath take it.
                  initialSlide={currentPhotoIndex}
                  afterChange={(index) => setCurrentPhotoIndex(index)}
                  arrows={true}
                  dots={false}
                  swipeToSlide={true}
                  draggable={true}
                  touchMove={true}
                  infinite={false}
                  speed={300}
                  easing="ease-out"
                  style={{ touchAction: 'auto' }}
                >
                  {sortedPhotos.map((photo: any) => (
                    <CarouselItem
                      key={photo.id}
                      imageUrl={photo?.imageUrl}
                      onClick={(url) => setViewerUrl(url)}
                    />
                  ))}
                </Carousel>
              </>
            )
            : (
              <img
                src={fullImageUrl}
                alt="tree image"
                style={{ cursor: 'pointer' }}
                onClick={() => imageUrl && setViewerUrl(fullImageUrl)}
              />
            )
          }
          <div style={{ textAlign: 'left' }}>
            <p>
              <strong>Description</strong><br />
              {tree.numberPlanted}x&nbsp;
              {treeType.translations.latin}
            </p>
            <p>
              <strong>Type</strong><br />
              {treeType?.fruitTree ? 'fruit tree' : 'shade tree'}
            </p>
            <p>
              <strong>Altitude</strong><br />
              {tree.altitude ? `${Math.round(tree.altitude)} meter${Math.abs(Math.round(tree.altitude)) > 1 ? 's' : ''}` : '-'}
            </p>
          </div>
        </div>
      }

      <PhotoViewer url={viewerUrl} onClose={() => setViewerUrl(undefined)} />
    </Modal>
  );
}